import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  IconButton,
  Collapse,
  Divider,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Info as InfoIcon } from '@mui/icons-material';

interface ModernSectionProps {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  loading?: boolean;
  count?: number;
  description?: string;
  headerActions?: React.ReactNode;
  variant?: 'default' | 'accent' | 'subtle';
}

const ModernSection: React.FC<ModernSectionProps> = ({
  title,
  subtitle,
  icon: Icon,
  children,
  collapsible = false,
  defaultExpanded = true,
  loading = false,
  count,
  description,
  headerActions,
  variant = 'default',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Update expanded state when defaultExpanded prop changes
  React.useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'accent':
        return {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          '& .MuiTypography-root': { color: 'white' },
          '& .MuiIconButton-root': { color: 'white' },
        };
      case 'subtle':
        return {
          bgcolor: isDark ? 'grey.900' : 'grey.50',
          border: '1px solid',
          borderColor: isDark ? 'grey.700' : 'grey.200',
        };
      default:
        return {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isDark ? '#30363D' : 'divider',
        };
    }
  };

  return (
    <Card
      sx={{
        mb: 0,
        mt: 0,
        borderRadius: 0,
        ...getVariantStyles(),
        '&:hover': {
          boxShadow: 'none',
          transform: 'none',
        },
        transition: 'none',
        boxShadow: 'none',
      }}
    >
      {/* Header */}
      <CardContent sx={{ pb: expanded ? 2 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            {Icon && (
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor:
                    variant === 'accent' ? 'rgba(255,255,255,0.2)' : isDark ? 'rgba(164,188,197,0.15)' : '#A4BCC5',
                  color: variant === 'accent' ? 'white' : isDark ? '#79C0FF' : '#3F5D66',
                }}
              >
                <Icon fontSize="small" />
              </Box>
            )}

            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={subtitle ? 0.5 : 0}>
                <Typography variant="h6" fontWeight="bold">
                  {title}
                </Typography>

                {loading && count === undefined ? (
                  <CircularProgress size={16} sx={{ ml: 0.5, color: '#FF564F' }} />
                ) : count !== undefined ? (
                  loading ? (
                    <Chip
                      icon={<CircularProgress size={12} sx={{ color: '#FF564F' }} />}
                      label="..."
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 32, height: 24, fontWeight: 'bold' }}
                    />
                  ) : (
                    <Chip
                      label={count}
                      size="small"
                      color={variant === 'accent' ? 'default' : 'primary'}
                      sx={{
                        minWidth: 32,
                        height: 24,
                        fontWeight: 'bold',
                        ...(variant === 'accent' && {
                          bgcolor: 'rgba(255,255,255,0.9)',
                          color: 'primary.main',
                        }),
                      }}
                    />
                  )
                ) : null}

                {!expanded && collapsible && !loading && count !== undefined && count > 0 && (
                  <Chip
                    label={`Click to view ${count} items`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{
                      fontSize: '0.7rem',
                      height: 24,
                      opacity: 0.8,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.8 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.8 },
                      },
                    }}
                  />
                )}

                {description && (
                  <Tooltip title={description} arrow>
                    <IconButton size="small" sx={{ ml: 0.5 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                  {!expanded && collapsible && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ ml: 1, fontWeight: 'medium', cursor: 'pointer', color: isDark ? '#79C0FF' : '#3F5D66' }}
                      onClick={() => setExpanded(true)}
                    >
                      → Expand to view details
                    </Typography>
                  )}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {headerActions}

            {collapsible && (
              <Tooltip title={expanded ? 'Collapse section' : 'Expand section'} arrow>
                <IconButton
                  onClick={() => setExpanded(!expanded)}
                  sx={{
                    transition: 'transform 0.2s ease-in-out',
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    bgcolor: !expanded ? (isDark ? 'rgba(164,188,197,0.15)' : '#A4BCC5') : 'transparent',
                    color: !expanded ? (isDark ? '#79C0FF' : '#3F5D66') : 'inherit',
                    '&:hover': {
                      bgcolor: !expanded ? (isDark ? 'rgba(63,93,102,0.4)' : '#3F5D66') : 'action.hover',
                      color: !expanded ? 'white' : 'inherit',
                    },
                  }}
                >
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {expanded && subtitle && <Divider sx={{ mt: 2, mb: 2 }} />}
      </CardContent>

      {/* Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

export default ModernSection;
